import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { StoreAuxService } from '../../services/storeAux/store-aux.service';
import { environment } from '../../../environments/environment';

import {
  CatalogListResponse,
  Company,
  CompanyListFilters,
  CompanyRecord,
  CompaniesListResponse,
  CompanyStatusesResponse,
  Contact,
  ExternalIdAvailability,
  FileUploadData,
  FinancesConfig,
  OwnersSearchResponse,
  OwnerValidationResponse,
  PurchaseOrder,
  PurchaseOrderCreateRequest,
  PurchaseOrderDetail,
  PurchaseOrderSearchFilters,
  PurchaseOrderSearchResponse,
  PurchaseOrderUpdateRequest
} from '../models/makers-finances-mgr.models';

@Injectable()
export class MakersFinancesMgrService {
  private readonly BASE_PATH = `${environment.urlGatewayInt}/makers-finances-mgr`;

  readonly config$ = new BehaviorSubject<FinancesConfig | null>(null);

  constructor(
    private readonly http: HttpClient,
    private readonly storeAux: StoreAuxService
  ) {}

  resolveCountry(country: string): string {
    return country === 'DEV' ? 'CO' : country;
  }

  getConfigs(country: string): Observable<{ message: string; data: FinancesConfig }> {
    const params = new HttpParams({ fromObject: { country: this.resolveCountry(country).toLowerCase() } });
    return this.http.get<{ message: string; data: FinancesConfig }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/configs`),
      { params }
    ).pipe(tap(res => this.config$.next(res.data)));
  }

  searchCompanies(
    country: string,
    q?: string,
    options: { agencyOnly?: boolean } = {}
  ): Observable<{ message: string; data: Company[] }> {
    let fromObject: Record<string, string> = { country: this.resolveCountry(country) };
    if (q) {
      fromObject = { ...fromObject, q };
    }
    if (options.agencyOnly !== undefined) {
      fromObject = { ...fromObject, agency_only: String(options.agencyOnly) };
    }
    const params = new HttpParams({ fromObject });
    return this.http.get(
      this.storeAux.getCountry(`${this.BASE_PATH}/companies/search`),
      { params, responseType: 'text' }
    ).pipe(map(text => this._parseSafeJson<{ message: string; data: Company[] }>(text)));
  }

  // POST /companies/search (gateway) → backend /companies/list — paginated company list for the
  // Companies tab. The gateway exposes this endpoint at /companies/search (not /makers-finances-mgr/
  // companies/list), so the URL is built from urlGatewayInt directly. `page`/`limit` travel in the
  // query string; filters (q, statuses, types, owners) travel in the body — only present, non-null
  // fields are sent. The email + country headers come from _poHeaders().
  // Records are normalized (status/type → snake_case codes) so the component can use them directly
  // with COMPANY_STATUS / COMPANY_TYPE constants and the SCSS badge classes.
  getCompanies(
    filters: CompanyListFilters,
    page = 1,
    limit = 10,
  ): Observable<CompaniesListResponse> {
    const params = new HttpParams({ fromObject: { page: String(page), limit: String(limit) } });
    const body = Object.fromEntries(
      Object.entries(filters).filter(([, v]) => v !== undefined && v !== null),
    );
    return this.http.post<CompaniesListResponse>(
      this.storeAux.getCountry(`${environment.urlGatewayInt}/companies/search`),
      body,
      { params, headers: this._poHeaders().set('Content-Type', 'application/json') },
    ).pipe(
      map(res => ({
        ...res,
        data: {
          ...res.data,
          records: res.data.records.map(r => this._normalizeRecord(r)),
        },
      })),
    );
  }

  // GET /companies/:id — company detail for the Companies tab. Parsed as text + _parseSafeJson so
  // the 19-digit legacy_zoho_id is preserved as a string (same as getPurchaseOrder). The record
  // is normalized (status/type → snake_case) so the view can use COMPANY_STATUS / COMPANY_TYPE.
  getCompany(id: number): Observable<{ message: string; data: CompanyRecord }> {
    return this.http.get(
      this.storeAux.getCountry(`${this.BASE_PATH}/companies/${id}`),
      { headers: this._poHeaders(), responseType: 'text' },
    ).pipe(map(text => {
      const res = this._parseSafeJson<{ message: string; data: CompanyRecord }>(text);
      return { ...res, data: this._normalizeRecord(res.data) };
    }));
  }

  searchContacts(companyId: string, q?: string): Observable<{ message: string; data: Contact[] }> {
    let fromObject: Record<string, string> = { company_id: companyId, country: this.resolveCountry(this.storeAux.country) };
    if (q) {
      fromObject = { ...fromObject, q };
    }
    const params = new HttpParams({ fromObject });
    return this.http.get(
      this.storeAux.getCountry(`${this.BASE_PATH}/contacts/search`),
      { params, responseType: 'text' }
    ).pipe(map(text => this._parseSafeJson<{ message: string; data: Contact[] }>(text)));
  }

  previewFile(key: string, country: string): Observable<Blob> {
    const params = new HttpParams({ fromObject: { key, country } });
    return this.http.get(
      this.storeAux.getCountry(`${this.BASE_PATH}/files/preview`),
      { params, responseType: 'blob' }
    );
  }

  uploadFile(file: File, country: string): Observable<{ message: string; data: FileUploadData }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('country', this.resolveCountry(country));
    return this.http.post<{ message: string; data: FileUploadData }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/files/upload`),
      formData
    );
  }

  searchOwners(country: string, page = 1, limit = 10, q?: string): Observable<OwnersSearchResponse> {
    let fromObject: Record<string, string> = { country: this.resolveCountry(country), page: String(page), limit: String(limit) };
    if (q) {
      fromObject = { ...fromObject, q };
    }
    const params = new HttpParams({ fromObject });
    return this.http.get<OwnersSearchResponse>(
      this.storeAux.getCountry(`${this.BASE_PATH}/owners/search`),
      { params, headers: this._poHeaders() }
    );
  }

  // GET /owners/validate?email=... — validates that the owner email exists in Zoho.
  validateOwner(email: string): Observable<OwnerValidationResponse> {
    const params = new HttpParams({ fromObject: { email } });
    return this.http.get<OwnerValidationResponse>(
      this.storeAux.getCountry(`${this.BASE_PATH}/owners/validate`),
      { params, headers: this._poHeaders() },
    );
  }

  // HU3 — POST /purchase-orders/search?page=&limit=
  // page/limit travel in the query string (the backend ignores them in the body via json:"-").
  // Filters travel in the body; only present fields are sent.
  searchPurchaseOrders(
    filters: PurchaseOrderSearchFilters,
    page = 1,
    limit = 10,
  ): Observable<{ message: string; data: PurchaseOrderSearchResponse }> {
    const params = new HttpParams({ fromObject: { page: String(page), limit: String(limit) } });
    const body = Object.fromEntries(
      Object.entries(filters).filter(([, v]) => v !== undefined && v !== null),
    );
    return this.http.post<{ message: string; data: PurchaseOrderSearchResponse }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders/search`),
      this._stringifyBody(body),
      { params, headers: this._poHeaders().set('Content-Type', 'application/json') },
    );
  }

  // HU3 — GET /purchase-orders/:id
  // Parsed as text + _parseSafeJson so the 19-digit Zoho company_to_bill_id is preserved as a
  // string instead of being truncated by JSON.parse (same pattern as searchCompanies).
  getPurchaseOrder(id: number): Observable<{ message: string; data: PurchaseOrderDetail }> {
    return this.http.get(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders/${id}`),
      { headers: this._poHeaders(), responseType: 'text' },
    ).pipe(map(text => this._parseSafeJson<{ message: string; data: PurchaseOrderDetail }>(text)));
  }

  // PR #38: on-blur validation against GET /purchase-orders/external-id/availability.
  // companyToBillId is the Zoho int64 held as a string (see _stringifyBody) to avoid float
  // precision loss. excludeId is optional (only used when editing an existing PO).
  checkExternalIdAvailability(
    externalId: string,
    companyToBillId: string,
    country: string,
    excludeId?: number,
  ): Observable<{ message: string; data: ExternalIdAvailability }> {
    let fromObject: Record<string, string> = {
      external_id: externalId,
      company_to_bill_id: companyToBillId,
      country: this.resolveCountry(country),
    };
    if (excludeId != null) {
      fromObject = { ...fromObject, exclude_id: String(excludeId) };
    }
    const params = new HttpParams({ fromObject });
    return this.http.get<{ message: string; data: ExternalIdAvailability }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders/external-id/availability`),
      { params, headers: this._emailHeaders() },
    );
  }

  createPurchaseOrder(body: PurchaseOrderCreateRequest): Observable<{ message: string; data: PurchaseOrder }> {
    return this.http.post<{ message: string; data: PurchaseOrder }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders`),
      this._stringifyBody(body),
      { headers: this._poHeaders().set('Content-Type', 'application/json') }
    );
  }

  updatePurchaseOrder(id: number, body: PurchaseOrderUpdateRequest): Observable<{ message: string; data: PurchaseOrder }> {
    return this.http.put<{ message: string; data: PurchaseOrder }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders/${id}`),
      this._stringifyBody(body),
      { headers: this._poHeaders().set('Content-Type', 'application/json') }
    );
  }

  submitPurchaseOrder(id: number): Observable<{ message: string; data: PurchaseOrder }> {
    return this.http.post<{ message: string; data: PurchaseOrder }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders/${id}/submit`),
      null,
      { headers: this._poHeaders() }
    );
  }

  // POST /purchase-orders/:id/cancel — transitions a pre-approval PO to `canceled`.
  // `reason` is optional (reserved for audit). Returns the full PO representation.
  cancelPurchaseOrder(id: number, reason?: string): Observable<{ message: string; data: PurchaseOrder }> {
    const body = reason != null && reason !== '' ? { reason } : null;
    return this.http.post<{ message: string; data: PurchaseOrder }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders/${id}/cancel`),
      body,
      { headers: this._poHeaders().set('Content-Type', 'application/json') },
    );
  }

  // POST /purchase-orders/:id/close — transitions an approved PO to `closed`.
  closePurchaseOrder(id: number): Observable<{ message: string; data: PurchaseOrder }> {
    return this.http.post<{ message: string; data: PurchaseOrder }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders/${id}/close`),
      {},
      { headers: this._poHeaders().set('Content-Type', 'application/json') },
    );
  }

  // POST /purchase-orders/:id/reopen — transitions a closed PO back to `approved`.
  reopenPurchaseOrder(id: number): Observable<{ message: string; data: PurchaseOrder }> {
    return this.http.post<{ message: string; data: PurchaseOrder }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders/${id}/reopen`),
      null,
      { headers: this._poHeaders() },
    );
  }

  // POST /purchase-orders/:id/approve — approves a PO (client or paid_by_rappi).
  approvePurchaseOrder(id: number): Observable<{ message: string; data: PurchaseOrder }> {
    return this.http.post<{ message: string; data: PurchaseOrder }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders/${id}/approve`),
      null,
      { headers: this._poHeaders() },
    );
  }

  // POST /purchase-orders/:id/return-to-kam — returns a PO to the KAM with a required comment.
  returnToKam(id: number, comment: string): Observable<{ message: string; data: PurchaseOrder }> {
    return this.http.post<{ message: string; data: PurchaseOrder }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders/${id}/return-to-kam`),
      { comment },
      { headers: this._poHeaders().set('Content-Type', 'application/json') },
    );
  }

  // POST /purchase-orders/:id/resubmit — revalidates and resubmits a PO returned to KAM.
  // `comment` is optional (max 2000 chars).
  resubmitPurchaseOrder(id: number, comment?: string): Observable<{ message: string; data: PurchaseOrder }> {
    const body = comment != null && comment !== '' ? { comment } : null;
    return this.http.post<{ message: string; data: PurchaseOrder }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders/${id}/resubmit`),
      body,
      { headers: this._poHeaders().set('Content-Type', 'application/json') },
    );
  }

  // POST /purchase-orders/:id/retry-settlement — retries settlement registration.
  // Note: response has no `data` field, only `message`.
  retrySettlement(id: number): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders/${id}/retry-settlement`),
      null,
      { headers: this._poHeaders() },
    );
  }

  // POST /purchase-orders/:id/retry-trato — retries Trato contract creation.
  retryTrato(id: number): Observable<{ message: string; data: PurchaseOrder }> {
    return this.http.post<{ message: string; data: PurchaseOrder }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders/${id}/retry-trato`),
      null,
      { headers: this._poHeaders() },
    );
  }

  // POST /purchase-orders/:id/comments — adds a comment to a PO (no stage transition).
  // Response has no `data`, only `message`.
  addComment(id: number, body: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders/${id}/comments`),
      { body },
      { headers: this._poHeaders().set('Content-Type', 'application/json') },
    );
  }

  // POST /mdm-core/{country}/makers/search — MDM maker search for filter dropdowns.
  searchMakers(page: number, size: number, search: string): Observable<{ content: { id: number; code: string; name: string }[] }> {
    const url = `${this.storeAux.getCountry(environment.urlMdmCore)}/makers/search`;
    const params = new HttpParams({ fromObject: {
      page: String(page),
      size: String(size),
      direction: 'DESC',
      orderBy: 'createdAt',
    }});
    return this.http.post<{ content: { id: number; code: string; name: string }[] }>(
      url,
      search ? { name: search } : {},
      { params },
    );
  }

  getSourceTypes(page = 1, limit = 50): Observable<CatalogListResponse> {
    const params = new HttpParams({ fromObject: { page: String(page), limit: String(limit) } });
    return this.http.get<CatalogListResponse>(
      this.storeAux.getCountry(`${this.BASE_PATH}/source-types`),
      { params, headers: this._emailHeaders() }
    );
  }

  // GET /company-type-options — catalog of company types for the Companies tab filter.
  // Same shape as getSourceTypes (CatalogListResponse with records + pagination).
  getCompanyTypeOptions(page = 1, limit = 50): Observable<CatalogListResponse> {
    const params = new HttpParams({ fromObject: { page: String(page), limit: String(limit) } });
    return this.http.get<CatalogListResponse>(
      this.storeAux.getCountry(`${this.BASE_PATH}/company-type-options`),
      { params, headers: this._emailHeaders() }
    );
  }

  // GET /companies/statuses — catalog of company statuses for the Companies tab Status filter.
  // Requires the `country` header (resolved DEV → CO via _poHeaders). Unlike the other catalog
  // endpoints, this one returns a flat array (no pagination wrapper), and the backend's UPPER_CASE
  // codes are normalized to snake_case so they match COMPANY_STATUS and can be sent as the
  // `statuses` filter.
  getCompanyStatuses(): Observable<CompanyStatusesResponse> {
    return this.http.get<CompanyStatusesResponse>(
      this.storeAux.getCountry(`${this.BASE_PATH}/companies/statuses`),
      { headers: this._poHeaders() },
    ).pipe(
      map(res => ({
        ...res,
        data: res.data.map(s => ({ ...s, code: this._normalizeStatusCode(s.code) })),
      })),
    );
  }

  // Normalizes a backend status code (UPPER_CASE, possibly with spaces) to the snake_case code
  // used by COMPANY_STATUS — lowercase + spaces → "_". Mirrors the status normalization in
  // _normalizeRecord so filter codes match the codes returned by /companies/list.
  private _normalizeStatusCode(code: string): string {
    return code ? code.toLowerCase().replace(/\s+/g, '_') : code;
  }

  getTagOptions(page = 1, limit = 50): Observable<CatalogListResponse> {
    const params = new HttpParams({ fromObject: { page: String(page), limit: String(limit) } });
    return this.http.get<CatalogListResponse>(
      this.storeAux.getCountry(`${this.BASE_PATH}/tag-options`),
      { params, headers: this._emailHeaders() }
    );
  }

  getPoTypeOptions(page = 1, limit = 50): Observable<CatalogListResponse> {
    const params = new HttpParams({ fromObject: { page: String(page), limit: String(limit) } });
    return this.http.get<CatalogListResponse>(
      this.storeAux.getCountry(`${this.BASE_PATH}/po-type-options`),
      { params, headers: this._emailHeaders() }
    );
  }

  private _emailHeaders(): HttpHeaders {
    return new HttpHeaders({ email: localStorage.getItem('email') || '' });
  }

  // Normalizes backend type_of_company values (e.g. "Maker", "Service Provider") to the snake_case
  // codes used by COMPANY_TYPE. Multi-word variants are mapped explicitly; single-word values are
  // lowercased. Unknown values fall back to the lowercased input so new backend types surface
  // visibly (the type column renders blank via TYPE_LABELS[c.type] until a mapping is added).
  private static readonly TYPE_NORMALIZATION: Readonly<Record<string, string>> = {
    'maker':            'maker',
    'retailer':         'retailer',
    'maker & retailer': 'maker_retailer',
    'agency':           'agency',
    'service provider': 'service_provider',
    'service provider/partnerships': 'service_provider',
  };

  private _normalizeRecord(record: CompanyRecord): CompanyRecord {
    return {
      ...record,
      // Backend statuses use UPPER_CASE with spaces (e.g. "AWAITING FINANCIAL VALIDATION");
      // normalize to the snake_case codes used by COMPANY_STATUS (lowercase + spaces → "_").
      status: record.status ? record.status.toLowerCase().replace(/\s+/g, '_') : record.status,
      type_of_company: this._normalizeType(record.type_of_company),
    };
  }

  private _normalizeType(type: string): string {
    if (!type) {
      return type;
    }
    const key = type.toLowerCase();
    return MakersFinancesMgrService.TYPE_NORMALIZATION[key] ?? key;
  }

  // PR #56: all Purchase Order endpoints require a `country` header (ISO alpha-2).
  // resolveCountry maps DEV → CO.
  private _poHeaders(): HttpHeaders {
    return this._emailHeaders().set('country', this.resolveCountry(this.storeAux.country));
  }

  // Quotes integers with 16+ digits before JSON.parse to prevent float precision loss.
  private _parseSafeJson<T>(text: string): T {
    return JSON.parse(text.replace(/(:\s*)(\d{16,})/g, '$1"$2"'));
  }

  // Zoho IDs (company_to_bill_id, contact_id, agency_id) are 19-digit int64 values that exceed
  // Number.MAX_SAFE_INTEGER. JSON.stringify can't emit them at full precision: a JS number gets
  // truncated, and a string gets quoted (which Go's *int64 rejects with 400). So we stringify
  // with the IDs as strings, then unquote just these numeric fields to emit raw numeric literals
  // that Go unmarshals to int64 at full precision. Only all-digit values are unquoted; a
  // malformed value stays quoted and surfaces as a visible 400 instead of a silent truncation.
  private static readonly INT64_FIELDS: ReadonlyArray<string> = [
    'company_to_bill_id', 'contact_id', 'agency_id',
  ];

  // Int64 IDs that travel as string[] — each element is unquoted so Go unmarshals []int64 at full precision.
  private static readonly INT64_ARRAY_FIELDS: ReadonlyArray<string> = [
    'company_ids',
  ];

  private _stringifyBody(body: object): string {
    let json = JSON.stringify(body);
    for (const field of MakersFinancesMgrService.INT64_FIELDS) {
      json = json.replace(new RegExp(`"${field}"\\s*:\\s*"(\\d+)"`, 'g'), `"${field}":$1`);
    }
    for (const field of MakersFinancesMgrService.INT64_ARRAY_FIELDS) {
      json = json.replace(new RegExp(`"${field}"\\s*:\\s*\\[([^\\]]*)\\]`, 'g'),
        (match, content: string) => `"${field}":[${content.replace(/"(\d+)"/g, '$1')}]`);
    }
    return json;
  }
}
